// Default site content — mirrors what the backend seeds so the UI renders
// beautifully even before the CMS API responds (or if the fetch fails).
import PG_IMAGES from '@/constants/publicImages';

export const DEFAULT_SITE_CONTENT = {
  hero: {
    badge: 'PADEL & COFFEE  ·  AL AHSA',
    headline_top: 'More',
    headline_bottom: 'Than a Game',
    eyebrow_line1: 'A  VIBRANT  COMMUNITY',
    eyebrow_line2: 'IN  THE  HEART  OF  AL  AHSA',
    body: "Padel Gems is a premier sports and lifestyle destination bringing people together through the world's fastest growing sport \u2014 padel. Set near the iconic Jabal Al Qarah, we combine world-class facilities, exceptional coaching, and a unique cafe-lounge experience.",
    cta_label: 'BE PART OF THE COMMUNITY',
    cta_target: 'contact',
    image_alt: 'Padel Gems courts at golden hour with Jabal Al Qarah behind',
    image_url: PG_IMAGES.courtSigns,
    bg_image_url: PG_IMAGES.aerialComplex,
    neon_lines: ['GOOD', 'PLAYERS', 'BETTER', 'PEOPLE'],
    script_text: 'Al Ahsa',
    script_sub: 'A UNIQUE SETTING',
    pills: [
      { icon: 'Users', label: 'PLAY' },
      { icon: 'Heart', label: 'CONNECT' },
      { icon: 'Gem', label: 'BELONG' },
    ],
  },
  about: {
    eyebrow: 'ABOUT  PADEL  GEMS',
    heading_top: 'Sport. Community.',
    heading_script: 'Lifestyle.',
    body: "We believe padel is more than a sport \u2014 it\u2019s a way to build friendships, stay active, and be part of something bigger. Padel Gems is designed to inspire individuals, families and businesses with a modern sporting environment in one of Saudi Arabia\u2019s most beautiful locations.",
    bg_image_url: PG_IMAGES.courtSigns,
    inset_image_url: PG_IMAGES.coffeeLounge,
    inset_alt: 'Padel Gems cafe lounge overlooking the courts at sunset',
    inset_label: 'ICONIC LOCATION',
    inset_sub: 'EXCEPTIONAL EXPERIENCES',
    stats: [
      { icon: 'Mountain', label: 'ICONIC', sub: 'Al Ahsa' },
      { icon: 'Coffee', label: 'CAFE', sub: 'Lounge' },
      { icon: 'Trophy', label: 'WORLD', sub: 'Class' },
    ],
  },
  services: {
    eyebrow: 'OUR  SERVICES',
    heading_top: 'Everything you need to',
    heading_script: 'play & belong',
    subhead: 'From premium court bookings to expert coaching and a signature cafe lounge \u2014 one destination, endless moments.',
    bg_image_url: PG_IMAGES.collage,
    cards: [
      { title: 'COURT BOOKINGS', body: 'Premium indoor & outdoor courts with easy online booking.', image_url: PG_IMAGES.playersAction },
      { title: 'COACHING & ACADEMY', body: 'Professional coaching for all levels \u2014 from beginners to advanced.', image_url: PG_IMAGES.courtSigns },
      { title: 'TOURNAMENTS & EVENTS', body: 'Exciting competitions and corporate events all year round.', image_url: PG_IMAGES.aerialComplex },
      { title: 'FOOD, BEVERAGE & SOCIAL', body: 'A welcoming cafe-lounge to relax, refuel and connect.', image_url: PG_IMAGES.coffeeLounge },
    ],
  },
  community: {
    heading_line1: 'A  HEALTHIER,',
    heading_line2: 'HAPPIER  COMMUNITY',
    heading_line3: 'THROUGH',
    heading_script: 'Padel.',
    body: 'Every rally builds a friendship. Every session builds strength. Padel Gems is where the community of Al Ahsa comes to move, connect and belong.',
    bg_image_url: PG_IMAGES.playersAction,
    pillars: [
      { icon: 'Leaf', title: 'HEALTHIER', sub: 'LIFESTYLES' },
      { icon: 'Users', title: 'STRONGER', sub: 'COMMUNITIES' },
      { icon: 'Gem', title: 'BRIGHTER', sub: 'TOMORROWS' },
    ],
  },
  footer: {
    brand: 'PADEL GEMS',
    tagline: 'PLAY  ·  CONNECT  ·  BELONG',
    location_line1: 'Near Jabal Al Qarah',
    location_line2: 'Al Ahsa, Saudi Arabia',
    socials: [
      { icon: 'Instagram', label: '@padelgems', url: 'https://instagram.com/padelgems' },
      { icon: 'Globe', label: 'www.padelgems.sa', url: 'https://padelgems.sa' },
    ],
    cta_label: "LET'S PLAY",
    cta_target: 'contact',
    copyright: '\u00a9 2026 PADEL GEMS  ·  ALL RIGHTS RESERVED',
  },
  navbar: {
    brand: 'PADEL GEMS',
    tagline: 'PLAY  ·  CONNECT  ·  BELONG',
    location_pill_line1: 'AL AHSA',
    location_pill_line2: 'SAUDI ARABIA',
    menu: [
      { label: 'HOME', target: 'home' },
      { label: 'THE PROJECT', target: 'about' },
      { label: 'OUR SERVICES', target: 'services' },
      { label: 'COMMUNITY', target: 'community' },
      { label: 'CONTACT', target: 'contact' },
    ],
  },
  marquee: {
    enabled: true,
    speed: 'medium',        // slow | medium | fast
    theme: 'green',         // green | cream | gold
    direction: 'left',      // left | right
    separator: 'diamond',   // dot | diamond | slash | none
    items: [
      { text: 'PLAY  ·  CONNECT  ·  BELONG', icon: 'Sparkles', image_url: '' },
      { text: 'WORLD-CLASS PADEL COURTS', icon: 'Trophy', image_url: '' },
      { text: 'SIGNATURE CAFE & LOUNGE', icon: 'Coffee', image_url: '' },
      { text: 'AL AHSA  ·  SAUDI ARABIA', icon: 'MapPin', image_url: '' },
      { text: 'BOOK YOUR COURT TODAY', icon: 'CalendarClock', image_url: '' },
      { text: 'COACHING FOR ALL LEVELS', icon: 'Users', image_url: '' },
    ],
  },
};

// Deep-merge helper for CMS overrides. Keeps `default` fields when API partial.
export function mergeContent(defaults, override) {
  if (!override || typeof override !== 'object') return defaults;
  if (Array.isArray(override)) return override.length ? override : defaults;
  const out = { ...defaults };
  for (const k of Object.keys(override)) {
    const v = override[k];
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      out[k] = mergeContent(defaults?.[k] || {}, v);
    } else if (v !== undefined && v !== null && v !== '') {
      out[k] = v;
    }
  }
  return out;
}

export default DEFAULT_SITE_CONTENT;
